import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState, useEffect, useCallback } from "react";
import {
  Download,
  Check,
  ChevronRight,
  ShieldCheck,
  Zap,
  MessageCircle,
  Maximize2,
  X,
  Sparkles,
  HelpCircle,
  ArrowRight,
  BookOpen,
  Droplets,
  Thermometer,
  Wind,
  Utensils,
  Flame,
  Award,
  MessageSquare,
  Factory,
  Plane,
  Snowflake,
  Truck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { storeDataKey } from "@/lib/utils/store-i18n";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Section } from "@/components/layout/Page";
import { AnimatedIncubationCone } from "@/components/visuals/AnimatedIncubationCone";
import { AnimatedJapanShipment } from "@/components/visuals/AnimatedJapanShipment";
import { AnimatedChlorellaBiology } from "@/components/visuals/AnimatedChlorellaBiology";
import { AnimatedDecapIllustration } from "@/components/visuals/AnimatedDecapIllustration";
import { DecapNutritionalInfographic } from "@/components/visuals/DecapNutritionalInfographic";
import japanFlagSvg from "@/assets/icons/japan-flag.svg";
import { getProduct, getCategory } from "@/lib/mock/products";
import { FAQS } from "@/lib/mock/faqs";
import { createWhatsAppUrl } from "@/lib/utils/whatsapp";
import { trackWhatsappClick } from "@/lib/analytics-events";
import type { Product, ProductCategory } from "@/lib/types";
import { RelatedProductsWidget } from "@/components/RelatedProductsWidget";
import { GlossaryTermsBlock } from "@/components/GlossaryTermsBlock";
import { QuoteDrawer } from "@/components/layout/QuoteDrawer";
import { useI18n } from "@/context/I18nContext";
import { activeDict } from "@/lib/i18n-dict";
import { breadcrumbSchema, jsonLdScript } from "@/lib/utils/schema-lists";
import { localizedBrandTitle, localizedCompanyName, snippetDescription } from "@/lib/utils/seo";
import { faqHeadScriptFromPairs } from "@/lib/utils/faq-schema";
import { absoluteAssetUrl, localizedUrl } from "@/lib/utils/site-url";
import { activeDictLang } from "@/lib/i18n-dict";
import { publishNotFound } from "@/lib/utils/canonical-override";

/**
 * Product field lookup for head(), which runs outside React so it cannot use
 * the component-side tp() helper.
 *
 * Without this, head() read product.title straight from the English source in
 * products.ts while the H1 rendered the translated key — so every localized
 * product page shipped an English <title> over a translated heading. Reads the
 * request's merged dictionary, the same bridge translateStoreSsr() uses for
 * /shop.
 */
function tpSsr(slug: string, field: string, fallback: string): string {
  return activeDict()[`product.${slug}.${field}`] || fallback;
}

/**
 * The specification rows as head() sees them — the SSR twin of tpSpecs().
 *
 * Index-keyed like every spec override, so this walks the SOURCE array and
 * looks each index up rather than walking the dictionary: a locale that is
 * missing one row falls back per cell instead of dropping every row after it.
 */
function tpSsrSpecs(product: Product): { label: string; value: string }[] {
  const dict = activeDict();
  return (product.specifications ?? []).map((spec, i) => ({
    label: dict[`product.${product.slug}.specifications.${i}.label`] || spec.label,
    value: dict[`product.${product.slug}.specifications.${i}.value`] || spec.value,
  }));
}

/**
 * The FAQ pairs this page renders, resolved the way the component resolves
 * them — custom set per product, shared default otherwise, each translatable.
 *
 * head() and the accordion must agree: FAQPage markup is only valid for
 * questions the page actually shows, so both derive from this one function
 * rather than each rebuilding the key logic.
 */
function productFaqPairs(slug: string): { q: string; a: string }[] {
  const dict = activeDict();
  const custom = PRODUCT_FAQS[slug];
  if (custom) {
    return custom.map((item, i) => ({
      q: dict[`product.${slug}.faq.${i}.q`] || item.q,
      a: dict[`product.${slug}.faq.${i}.a`] || item.a,
    }));
  }
  // Shared entries translate through the knowledge base's own namespace, which
  // all eight non-English locales already carry — the old
  // productDetail.faq.default.* keys were a second namespace for the same three
  // questions.
  return DEFAULT_FAQS.map((entry) => ({
    q: dict[`faq.${entry.slug}.q`] || entry.q,
    a: dict[`faq.${entry.slug}.a`] || entry.a,
  }));
}

export const Route = createFileRoute("/products/$category/$slug")({
  loader: ({ params }) => {
    const product = getProduct(params.category, params.slug);
    const category = getCategory(params.category);
    if (!product || !category) {
      publishNotFound(
        `/products/${encodeURIComponent(params.category)}/${encodeURIComponent(params.slug)}`,
      );
      throw notFound();
    }
    return { product, category };
  },
  head: ({ loaderData, params }) => {
    const dict = activeDict();
    const product = loaderData?.product;
    // Localized title/tagline for meta, matching what the H1 actually renders.
    const title = product ? tpSsr(product.slug, "title", product.title) : "Product";
    const tagline = product ? tpSsr(product.slug, "tagline", product.tagline) : "";
    const description = product
      ? tpSsr(product.slug, "description", product.description || product.tagline)
      : "";
    // The snippet is the description, trimmed to whole sentences. It used to be
    // the tagline, which is written as a one-line strapline: 79-108 characters
    // against a ~155-character budget, and on five products it never named the
    // product at all ("Sashimi-quality genetics with high biosecurity...").
    const snippet = snippetDescription(description || tagline);
    // Keywords are a per-locale list, not a translated sentence. /tr/ was
    // emitting "fertilized Atlantic salmon eggs" — the English set — in both
    // <meta name="keywords"> and Product.keywords, so a Turkish page declared
    // no Turkish trade term. seoKeywords stays the English fallback.
    const keywordsMeta = product
      ? tpSsr(product.slug, "seoKeywords", (product.seoKeywords ?? []).join(", "))
      : "";
    const productSchema = product
      ? {
          "@context": "https://schema.org/",
          "@type": "Product",
          name: title,
          // The English name stays reachable as an alternate: buyers searching
          // the international trade name should still match a localized page.
          alternateName: [...new Set([product.title, ...(product.searchSynonyms ?? [])])].filter(
            (n) => n !== title,
          ),
          // Every image on the page, not just the primary. Google Images reads
          // this array, and a product photographed from several angles has more
          // to rank than one that publishes a single shot.
          image: [product.image, ...(product.gallery ?? [])]
            .filter((src): src is string => Boolean(src))
            .map(absoluteAssetUrl),
          description,
          keywords: keywordsMeta,
          category: product.category,
          url: localizedUrl(`/products/${params.category}/${params.slug}`),
          inLanguage: activeDictLang() ?? "en",
          brand: {
            "@type": "Brand",
            name: localizedCompanyName(),
          },
          // The specification table, in machine-readable form. Every pair is
          // already rendered on the page, so this restates what the buyer can
          // see rather than asserting anything new — which is the line the
          // Content Accuracy Gate draws.
          additionalProperty: tpSsrSpecs(product).map((spec) => ({
            "@type": "PropertyValue",
            name: spec.label,
            value: spec.value,
          })),
          // No `offers`. This catalogue carries no prices — it is a B2B
          // portfolio that quotes per enquiry — and the block that used to sit
          // here was an AggregateOffer with no price at all plus a hardcoded
          // offerCount of "100", a number nothing in the codebase produces.
          // schema.org requires lowPrice on an AggregateOffer, so that object
          // was invalid as well as invented, and docs/project/content-seo-plan.md:45 is
          // explicit: do not invent prices. A Product without offers is valid;
          // it forgoes price rich results, which this catalogue cannot earn
          // honestly anyway. /shop products, which have real Odoo prices, do
          // emit a complete Offer — see shop.$slug.tsx.
        }
      : null;

    const metaTitle = localizedBrandTitle(title);
    return {
      meta: [
        // "B2B" bought nothing and cost five characters of a ~60-character
        // budget that the product noun needs more — "Atlantic Fertilized
        // Salmon Eyed Eggs" does not fit alongside it.
        { title: metaTitle },
        { name: "description", content: snippet },
        ...(keywordsMeta ? [{ name: "keywords", content: keywordsMeta }] : []),
        // A product page is not a website. __root emits og:type=website for
        // every route, so all eighteen were declaring the wrong object type.
        { property: "og:type", content: "product" },
        { property: "og:title", content: metaTitle },
        { property: "og:description", content: snippet },
        // Absolute, and omitted rather than emitted empty. A root-relative
        // og:image still overrides the root's absolute one, so a bundled asset
        // path here left every product page with an image no social crawler
        // could resolve; an empty content attribute did the same.
        ...(product?.image
          ? [
              { property: "og:image", content: absoluteAssetUrl(product.image) },
              { name: "twitter:image", content: absoluteAssetUrl(product.image) },
            ]
          : []),
        // twitter:* otherwise falls through to the root's homepage values, so
        // every product shared on X showed the homepage title and description.
        { name: "twitter:title", content: metaTitle },
        { name: "twitter:description", content: snippet },
      ],
      scripts: productSchema
        ? [
            // Carries an id like its breadcrumb and FAQ siblings. Without one
            // the block is not deduped on client-side navigation, so moving
            // between two products left both Product objects in the document.
            jsonLdScript("ld-product", productSchema),
            // Matches the trail the page renders above the title. Without it
            // a result showed the raw path instead of Products > Category >
            // Product.
            jsonLdScript(
              "ld-breadcrumb",
              breadcrumbSchema([
                {
                  name: dict["productDetail.breadcrumb.products"] ?? "Products",
                  path: "/products",
                },
                {
                  name: dict[`cat.${params.category}`] ?? params.category,
                  path: `/products/${params.category}`,
                },
                { name: title },
              ]),
            ),
            faqHeadScriptFromPairs(
              productFaqPairs(params.slug),
              `/products/${params.category}/${params.slug}`,
            ),
          ]
        : [],
    };
  },
  component: ProductDetail,
});

type LoaderData = { product: Product; category: ProductCategory };

function ProductDetail() {
  const { product, category } = Route.useLoaderData() as LoaderData;
  const { t, lang } = useI18n();

  const tp = (field: string, defaultValue: string): string => {
    const key = `product.${product.slug}.${field}`;
    const res = t(key);
    return res === key ? defaultValue : res;
  };

  // Composition tables repeat nutrient names across products and tables
  // (Protein, EPA, Lysine...), so they are keyed by the English term rather
  // than per product — one translation serves every occurrence.
  const tc = (term: string): string => {
    const key = `composition.${storeDataKey(term)}`;
    const res = t(key);
    return res === key ? term : res;
  };

  const tpArray = (field: string, defaultArray: string[]): string[] => {
    const result: string[] = [];
    let idx = 0;
    while (true) {
      const key = `product.${product.slug}.${field}.${idx}`;
      const res = t(key);
      if (res === key) break;
      result.push(res);
      idx++;
    }
    return result.length > 0 ? result : defaultArray;
  };

  const tpSpecs = (defaultSpecs: { label: string; value: string }[]) => {
    return defaultSpecs.map((s, idx) => {
      const labelKey = `product.${product.slug}.specifications.${idx}.label`;
      const valKey = `product.${product.slug}.specifications.${idx}.value`;
      const l = t(labelKey);
      const v = t(valKey);
      return {
        label: l === labelKey ? s.label : l,
        value: v === valKey ? s.value : v,
      };
    });
  };

  const tpMetrics = (defaultMetrics: { label: string; value: string }[]) => {
    return defaultMetrics.map((m, idx) => {
      const labelKey = `product.${product.slug}.metrics.${idx}.label`;
      const valKey = `product.${product.slug}.metrics.${idx}.value`;
      const l = t(labelKey);
      const v = t(valKey);
      return {
        label: l === labelKey ? m.label : l,
        value: v === valKey ? m.value : v,
      };
    });
  };
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);

  // Combine product main image and gallery images into a unique list
  const allImages = [product.image, ...(product.gallery ?? [])].filter(
    (src, idx, arr): src is string => Boolean(src) && arr.indexOf(src) === idx,
  );

  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const currentImage = allImages[activeImageIndex] || product.image;

  const handlePrev = useCallback(() => {
    setActiveImageIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  }, [allImages.length]);

  const handleNext = useCallback(() => {
    setActiveImageIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  }, [allImages.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isLightboxOpen) return;
      if (e.key === "Escape") setIsLightboxOpen(false);
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isLightboxOpen, handlePrev, handleNext]);

  return (
    <>
      <section className="bg-surface-alt/60 border-b border-border/80">
        <div className="mx-auto max-w-7xl px-4 py-4 md:px-6">
          <nav className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
            <Link to="/products" className="hover:text-navy transition-colors">
              {t("productDetail.breadcrumb.products")}
            </Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <Link
              to="/products/$category"
              params={{ category: category.slug }}
              className="hover:text-navy transition-colors"
            >
              {t(`cat.${category.slug}`)}
            </Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-navy font-bold">{tp("title", product.title)}</span>
          </nav>
        </div>

        <div className="mx-auto grid max-w-7xl gap-10 px-4 pb-16 pt-4 md:grid-cols-2 md:px-6">
          <div>
            {/* Main Showcase Image Display Box */}
            <div className="relative aspect-[4/3] overflow-hidden rounded-3xl border border-white/80 bg-gradient-to-b from-navy/5 via-surface-alt to-teal/10 shadow-2xl p-6 md:p-8 flex items-center justify-center group">
              {/* Ambient Glow Effects */}
              <div className="absolute -top-20 -left-20 h-60 w-60 rounded-full bg-teal/20 blur-3xl pointer-events-none group-hover:scale-125 transition-transform duration-700" />
              <div className="absolute -bottom-20 -right-20 h-60 w-60 rounded-full bg-primary/10 blur-3xl pointer-events-none group-hover:scale-125 transition-transform duration-700" />

              {/* Fullscreen Zoom Trigger - Top Right */}
              {currentImage && (
                <button
                  type="button"
                  onClick={() => setIsLightboxOpen(true)}
                  aria-label={t("productDetail.gallery.zoomAriaLabel")}
                  className="absolute top-4 right-4 z-10 p-2.5 rounded-2xl bg-white/85 text-navy hover:bg-navy hover:text-white backdrop-blur-md shadow-lg border border-white/80 transition-all scale-95 hover:scale-105 active:scale-95 group/zoom"
                  title={t("productDetail.gallery.zoomTitle")}
                >
                  <Maximize2 className="h-4 w-4" />
                </button>
              )}

              {/* Main Product Image */}
              {currentImage ? (
                <div
                  className="relative h-full w-full flex items-center justify-center cursor-zoom-in"
                  onClick={() => setIsLightboxOpen(true)}
                >
                  {/* The LCP element on this page, so eager and high priority —
                      it was lazy, which defers the one image the page is
                      actually about. Alt carries the species: image search for
                      "Oncorhynchus kisutch eggs" should be able to find this. */}
                  <img
                    src={currentImage}
                    alt={
                      product.latinName
                        ? `${tp("title", product.title)} (${product.latinName})`
                        : tp("title", product.title)
                    }
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    className="max-h-full max-w-full object-contain filter drop-shadow-xl transition-all duration-500 group-hover:scale-108 group-hover:-translate-y-1"
                  />
                </div>
              ) : (
                <div className="flex h-full items-center justify-center font-display text-6xl font-bold text-navy/20">
                  {product.title.slice(0, 2)}
                </div>
              )}

              {/* Navigation Arrows overlay */}
              {allImages.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={handlePrev}
                    aria-label={t("productDetail.gallery.previousImage")}
                    className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-2.5 text-navy opacity-80 backdrop-blur-md transition-all hover:bg-navy hover:text-white hover:scale-110 hover:opacity-100 shadow-xl border border-white/50"
                  >
                    <ChevronRight className="h-5 w-5 rotate-180" />
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    aria-label={t("productDetail.gallery.nextImage")}
                    className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-2.5 text-navy opacity-80 backdrop-blur-md transition-all hover:bg-navy hover:text-white hover:scale-110 hover:opacity-100 shadow-xl border border-white/50"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>

                  {/* Active Image Indicator Counter */}
                  <div className="absolute bottom-4 right-4 rounded-full bg-navy/85 px-3 py-1 text-[11px] font-mono font-bold text-white backdrop-blur-md shadow-md border border-white/20">
                    {activeImageIndex + 1} / {allImages.length}
                  </div>
                </>
              )}
            </div>

            {/* Thumbnail Navigation Gallery Bar */}
            {allImages.length > 1 && (
              <div className="mt-5 flex flex-wrap items-center gap-3">
                {allImages.map((src, index) => {
                  const isActive = index === activeImageIndex;
                  return (
                    <button
                      key={src}
                      type="button"
                      onClick={() => setActiveImageIndex(index)}
                      className={`relative aspect-square w-20 overflow-hidden rounded-2xl border bg-background p-2 shadow-sm transition-all focus:outline-none ${
                        isActive
                          ? "border-primary ring-2 ring-primary/40 scale-105 shadow-md bg-surface-alt"
                          : "border-border/80 hover:border-navy/50 opacity-70 hover:opacity-100 hover:scale-100"
                      }`}
                    >
                      {/* Not alt="": these thumbnails are the only place most
                          gallery images appear in the served HTML, and the
                          button has no other text, so an empty alt left it
                          unnamed for a screen reader and the image
                          undescribed for image search. */}
                      <img
                        src={src}
                        alt={`${tp("title", product.title)} — ${index + 1}`}
                        className="h-full w-full object-contain"
                        loading="lazy"
                      />
                      {isActive && (
                        <div className="absolute bottom-0 inset-x-0 h-1 bg-primary rounded-full" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Fullscreen Lightbox Zoom Modal */}
          {isLightboxOpen && currentImage && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-xl p-4 md:p-8 animate-in fade-in duration-200">
              <button
                type="button"
                onClick={() => setIsLightboxOpen(false)}
                className="absolute top-6 right-6 z-50 rounded-full bg-white/20 p-3 text-white hover:bg-white hover:text-black transition-all shadow-2xl"
                aria-label={t("productDetail.lightbox.closeAriaLabel")}
              >
                <X className="h-6 w-6" />
              </button>

              <div className="relative max-h-[90vh] max-w-[90vw] flex items-center justify-center">
                <img
                  src={currentImage}
                  alt={tp("title", product.title)}
                  className="max-h-[85vh] max-w-[85vw] object-contain drop-shadow-2xl rounded-2xl"
                />

                {allImages.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={handlePrev}
                      className="absolute left-2 md:-left-12 top-1/2 -translate-y-1/2 rounded-full bg-white/20 p-3 text-white hover:bg-white hover:text-black transition-all"
                    >
                      <ChevronRight className="h-6 w-6 rotate-180" />
                    </button>
                    <button
                      type="button"
                      onClick={handleNext}
                      className="absolute right-2 md:-right-12 top-1/2 -translate-y-1/2 rounded-full bg-white/20 p-3 text-white hover:bg-white hover:text-black transition-all"
                    >
                      <ChevronRight className="h-6 w-6" />
                    </button>
                  </>
                )}

                <div className="absolute bottom-[-2rem] text-center text-xs font-bold text-white/80 font-mono">
                  {tp("title", product.title)} — {activeImageIndex + 1}{" "}
                  {t("productDetail.lightbox.of")} {allImages.length} (
                  {t("productDetail.lightbox.pressEscToClose")})
                </div>
              </div>
            </div>
          )}

          <div className="space-y-6">
            <div>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {tpArray("tags", product.tags).map((t) => (
                  <span
                    key={t}
                    className="rounded-lg bg-teal/20 px-2.5 py-1 text-xs font-bold text-navy border border-teal/40"
                  >
                    {t}
                  </span>
                ))}
              </div>
              <h1 className="font-display text-3xl font-bold text-navy md:text-4xl leading-tight">
                {tp("title", product.title)}
              </h1>
              {product.latinName && (
                <p className="mt-1 font-display italic text-base text-muted-foreground">
                  {product.latinName}
                </p>
              )}
              <p className="mt-3 text-base text-muted-foreground leading-relaxed">
                {tp("tagline", product.tagline)}
              </p>
              {product.slug === "super-fresh-chlorella-v12" && (
                <span className="mt-3 inline-flex items-center gap-2 rounded-full bg-surface-alt px-3 py-1 text-xs font-bold text-navy border border-border/60">
                  <img
                    src={japanFlagSvg}
                    alt={t("common.alt.flagJapan")}
                    className="h-3.5 w-5 rounded object-cover shadow-sm border border-border/40"
                  />
                  {t("productDetail.sv12Japan.badge")}
                </span>
              )}
            </div>

            {/* Highlights, finally rendered. Above the metric tiles because a
                buyer scanning the page reads claims before numbers, and this is
                the first block on the page written in their language rather
                than the lab's. */}
            {tpArray("highlights", product.highlights ?? []).length > 0 && (
              <ul className="space-y-2">
                {tpArray("highlights", product.highlights ?? []).map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-navy/90">
                    <Check
                      className="h-4 w-4 shrink-0 text-emerald-500 mt-0.5"
                      aria-hidden="true"
                    />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            )}

            {product.category !== "seafood" && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {tpMetrics(product.metrics).map((m) => (
                  <div key={m.label} className="glass-card rounded-xl p-4 border border-border/80">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      {m.label}
                    </div>
                    <div className="mt-1 font-display text-xl font-bold text-navy">{m.value}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="rounded-2xl bg-background p-5 border border-border/80 shadow-sm space-y-4">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                {/* SPF (specific-pathogen-free) is an animal-health status. Shown on
                    every product it read as boilerplate on wheat gluten and chlorella
                    (VARS-169), so it is limited to the salmonid ova it describes. */}
                {product.slug.endsWith("salmon-egg") ? (
                  <span className="flex items-center gap-1.5 text-mint-ink font-bold">
                    <ShieldCheck className="h-4 w-4" /> {t("productDetail.badges.labCertified")}
                  </span>
                ) : (
                  <span />
                )}
                <span className="flex items-center gap-1 font-semibold text-navy">
                  <Zap className="h-3.5 w-3.5 text-primary" />{" "}
                  {t("productDetail.badges.b2bPricing")}
                </span>
              </div>
              <div className="space-y-3 pt-2">
                {/* Tier 1: Main Primary Actions */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button
                    size="lg"
                    onClick={() => setQuoteOpen(true)}
                    className="rounded-xl bg-primary text-primary-foreground font-bold hover:bg-primary/90 shadow-lg shadow-primary/20 py-6 text-base"
                  >
                    <ArrowRight className="mr-2 h-5 w-5" />
                    {t("productCard.quote")}
                  </Button>

                  <a
                    href={createWhatsAppUrl({
                      productTitle: tp("title", product.title),
                      categoryTitle: t(`cat.${category.slug}`),
                      latinName: product.latinName,
                      pageUrl: typeof window !== "undefined" ? window.location.href : undefined,
                    })}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackWhatsappClick({ topic: "product-detail", locale: lang })}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3.5 text-base font-bold text-white shadow-lg shadow-emerald-600/20 hover:bg-emerald-700 transition-colors"
                  >
                    <MessageSquare className="h-5 w-5 fill-current" />
                    <span>{t("productDetail.actions.whatsappQuote")}</span>
                  </a>
                </div>

                {/* Tier 2: Sub-Actions Utility Bar */}
                <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-2xl bg-surface-alt/70 border border-border/60 backdrop-blur-sm">
                  {/* The formal RFQ link was here. Two routes to the same
                      enquiry, side by side, made the buyer choose between them
                      for no reason — the drawer above does the same job without
                      leaving the product. /request-quote is still in the header
                      nav and linked from eight other pages, so it stays
                      crawlable. */}
                  {product.pdfUrl && (
                    <a
                      href={product.pdfUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-xs font-bold text-navy hover:text-primary transition-colors border-l border-border/80 pl-3"
                    >
                      <Download className="h-4 w-4 text-emerald-600" />
                      <span>
                        {product.pdfLabel
                          ? tp("pdfLabel", product.pdfLabel)
                          : t("productDetail.actions.downloadDatasheet")}
                      </span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Section>
        {/* Description leads. It is the tab a buyer reads first and the one
            carrying the prose a search engine has most use for — it was last,
            behind seven others. Every panel is forceMount so all of them are
            in the server-rendered HTML; the inactive ones are hidden by CSS
            only, never with the `hidden` attribute, so their content stays
            crawlable. */}
        <Tabs defaultValue="desc" className="w-full">
          <TabsList className="bg-surface-alt p-1 rounded-xl flex-wrap h-auto gap-1">
            <TabsTrigger value="desc" className="rounded-lg font-bold text-xs">
              {t("productDetail.tabs.description")}
            </TabsTrigger>
            <TabsTrigger value="specs" className="rounded-lg font-bold text-xs">
              {t("productDetail.tabs.specifications")}
            </TabsTrigger>
            {product.category === "seafood" && product.metrics?.length > 0 && (
              <TabsTrigger value="nutrition" className="rounded-lg font-bold text-xs">
                {t("productDetail.tabs.nutrition")}
              </TabsTrigger>
            )}
            {ROTIFER_PROTOCOLS[product.slug] && (
              <TabsTrigger value="rotifer" className="rounded-lg font-bold text-xs">
                {t("productDetail.tabs.rotifer")}
              </TabsTrigger>
            )}
            {PRODUCT_DETAILED_TABLES[product.slug] && (
              <TabsTrigger value="composition" className="rounded-lg font-bold text-xs">
                {t("productDetail.tabs.composition")}
              </TabsTrigger>
            )}
            {(product.slug === "artemia" || product.slug === "decapsulated-dry-artemia-cysts") && (
              <TabsTrigger value="hatching-protocol" className="rounded-lg font-bold text-xs">
                {t("productDetail.tabs.hatchingProtocol")}
              </TabsTrigger>
            )}
            {product.exportForms && product.exportForms.length > 0 && (
              <TabsTrigger value="export-forms" className="rounded-lg font-bold text-xs">
                {t("productDetail.tabs.exportForms")}
              </TabsTrigger>
            )}
            {product.culinaryInfo && (
              <TabsTrigger value="culinary" className="rounded-lg font-bold text-xs">
                {t("productDetail.tabs.culinary")}
              </TabsTrigger>
            )}
            <TabsTrigger value="apps" className="rounded-lg font-bold text-xs">
              {t("productDetail.tabs.applications")}
            </TabsTrigger>
            <TabsTrigger value="storage" className="rounded-lg font-bold text-xs">
              {t("productDetail.tabs.storage")}
            </TabsTrigger>
          </TabsList>

          <TabsContent forceMount value="desc" className="mt-6">
            <div className="glass-card rounded-2xl p-6 border border-border/80 space-y-4 text-xs leading-relaxed text-navy/80">
              {/* The one H2 that names the product. The outline used to run
                  H1 -> H3 (every panel) -> H2 (FAQ) -> H2 (footer nav), so the
                  footer's link columns were the highest-level headings on the
                  page after the title and nothing between H1 and the FAQ
                  described the product at all. Composed from the title plus the
                  existing tab label rather than a new "About {x}" key, because
                  t() has no interpolation and the word order would not survive
                  eight other languages. */}
              <h2 className="font-display text-lg font-bold text-navy">
                {tp("title", product.title)} — {t("productDetail.tabs.description")}
              </h2>
              <p className="font-medium text-sm leading-relaxed text-navy">
                {tp("description", product.description)}
              </p>
              {tpArray("longDescription", product.longDescription ?? []).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
              <GlossaryTermsBlock
                headingKey="glossary.mentions.headingProduct"
                text={[
                  tp("description", product.description),
                  ...tpArray("longDescription", product.longDescription ?? []),
                ].join(" ")}
              />
            </div>
          </TabsContent>

          <TabsContent forceMount value="specs" className="mt-6">
            <div className="rounded-2xl border border-border/80 p-6 glass-card space-y-4">
              <h2 className="font-display text-lg font-bold text-navy">
                {t("productDetail.specs.heading")}
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {tpSpecs(product.specifications).map((s, i) => (
                  <div
                    key={i}
                    className="flex justify-between items-center p-3 rounded-xl bg-surface-alt/60 border border-border/40"
                  >
                    <span className="text-xs font-semibold text-muted-foreground">{s.label}</span>
                    <span className="text-xs font-bold text-navy">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {product.category === "seafood" && product.metrics?.length > 0 && (
            <TabsContent forceMount value="nutrition" className="mt-6">
              <div className="rounded-2xl border border-border/80 p-6 glass-card space-y-4">
                <h2 className="font-display text-lg font-bold text-navy">
                  {t("productDetail.nutrition.heading")}
                </h2>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {tpMetrics(product.metrics).map((m) => (
                    <div
                      key={m.label}
                      className="rounded-xl bg-surface-alt/60 border border-border/40 p-4"
                    >
                      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        {m.label}
                      </div>
                      <div className="mt-1 font-display text-xl font-bold text-navy">{m.value}</div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed border-t border-border/60 pt-4">
                  {t("productDetail.nutrition.disclaimer")}
                </p>
              </div>
            </TabsContent>
          )}

          {ROTIFER_PROTOCOLS[product.slug] && (
            <TabsContent forceMount value="rotifer" className="mt-6">
              <div className="rounded-2xl border border-border/80 p-6 glass-card space-y-6">
                <h2 className="font-display text-lg font-bold text-navy">
                  {t("productDetail.tabs.rotifer")}
                </h2>
                <p className="text-xs leading-relaxed text-navy/80">
                  {tp("rotifer.intro", ROTIFER_PROTOCOLS[product.slug].intro)}
                </p>

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {ROTIFER_PROTOCOLS[product.slug].conditions.map((c, i) => (
                    <div
                      key={i}
                      className="rounded-xl bg-surface-alt/60 border border-border/40 p-3"
                    >
                      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        {tp(`rotifer.condition.${i}.label`, c.label)}
                      </div>
                      <div className="mt-1 text-xs font-bold text-navy">
                        {tp(`rotifer.condition.${i}.value`, c.value)}
                      </div>
                    </div>
                  ))}
                </div>

                {ROTIFER_PROTOCOLS[product.slug].steps && (
                  <ol className="space-y-2 text-xs leading-relaxed text-navy/80">
                    {ROTIFER_PROTOCOLS[product.slug].steps?.map((step, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="mt-0.5 shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
                          {i + 1}
                        </span>
                        <span>{tp(`rotifer.step.${i}`, step)}</span>
                      </li>
                    ))}
                  </ol>
                )}

                {ROTIFER_PROTOCOLS[product.slug].tables.map((table, tIdx) => (
                  <div key={tIdx} className="space-y-3">
                    <h3 className="font-display text-base font-bold text-navy">
                      {tc(table.title)}
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[480px] text-xs text-start">
                        {table.headers && (
                          <thead className="bg-surface-alt text-navy font-bold border-b border-border/60">
                            <tr>
                              {table.headers.map((h, hi) => (
                                <th key={hi} className="p-2">
                                  {tc(h)}
                                </th>
                              ))}
                            </tr>
                          </thead>
                        )}
                        <tbody>
                          {table.rows.map((row, ri) => (
                            <tr key={ri} className="border-b border-border/40">
                              <td className="p-2 font-semibold text-navy">{tc(row.col1)}</td>
                              <td className="p-2 text-muted-foreground">{row.col2}</td>
                              {row.col3 !== undefined && (
                                <td className="p-2 text-muted-foreground">{tc(row.col3)}</td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}

                <p className="text-[11px] text-muted-foreground border-t border-border/60 pt-3">
                  {t("productDetail.rotifer.sourceNote")}
                </p>
              </div>
            </TabsContent>
          )}

          {PRODUCT_DETAILED_TABLES[product.slug] && (
            <TabsContent forceMount value="composition" className="mt-6">
              <div className="space-y-6">
                {/* Panel-level heading so the individual table titles below sit
                    under an H2 instead of floating at H3 with no parent. */}
                <h2 className="font-display text-lg font-bold text-navy">
                  {t("productDetail.tabs.composition")}
                </h2>
                {PRODUCT_DETAILED_TABLES[product.slug].map((table, tIdx) => (
                  <div
                    key={tIdx}
                    className="rounded-2xl border border-border/80 p-6 glass-card space-y-4"
                  >
                    <h3 className="font-display text-lg font-bold text-navy">{tc(table.title)}</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[480px] text-xs text-start">
                        <thead className="bg-surface-alt text-navy font-bold border-b border-border/60">
                          <tr>
                            {table.headers?.map((h, hIdx) => (
                              <th key={hIdx} className="px-4 py-3">
                                {tc(h)}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border/40">
                          {table.rows.map((r, rIdx) => (
                            <tr key={rIdx} className="hover:bg-surface-alt/40 transition-colors">
                              <td className="px-4 py-2.5 font-semibold text-navy">{tc(r.col1)}</td>
                              <td className="px-4 py-2.5 font-bold text-primary">{r.col2}</td>
                              {r.col3 && <td className="px-4 py-2.5 text-navy/80">{r.col3}</td>}
                              {r.col4 && (
                                <td className="px-4 py-2.5 text-navy font-medium">{r.col4}</td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          )}

          {(product.slug === "artemia" || product.slug === "decapsulated-dry-artemia-cysts") && (
            <TabsContent forceMount value="hatching-protocol" className="mt-6">
              <div className="rounded-2xl border border-border/80 p-6 glass-card space-y-6">
                <div className="flex items-center justify-between border-b border-border/60 pb-4">
                  <div>
                    <h2 className="font-display text-xl font-bold text-navy flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-amber-500" />
                      {t("productDetail.hatching.heading")}
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t("productDetail.hatching.subheading")}
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {ARTEMIA_HATCHING_STEPS.map((s, idx) => (
                    <div
                      key={idx}
                      className="p-4 rounded-xl bg-surface-alt/60 border border-border/50 space-y-2 hover:border-primary/40 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-primary uppercase tracking-wider bg-primary/10 px-2 py-0.5 rounded-full">
                          {t(`productDetail.hatching.step${idx + 1}.step`)}
                        </span>
                      </div>
                      <h4 className="font-bold text-sm text-navy">
                        {t(`productDetail.hatching.step${idx + 1}.title`)}
                      </h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {t(`productDetail.hatching.step${idx + 1}.content`)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          )}

          {product.exportForms && product.exportForms.length > 0 && (
            <TabsContent forceMount value="export-forms" className="mt-6">
              <div className="space-y-6">
                {/* Parent for the per-format H3s and the documents block. */}
                <h2 className="font-display text-lg font-bold text-navy">
                  {t("productDetail.tabs.exportForms")}
                </h2>
                {product.exportForms.map((form, fi) => (
                  <div
                    key={form.key}
                    id={`format-${form.key}`}
                    className="rounded-2xl border border-border/80 p-6 glass-card space-y-4"
                  >
                    <div className="space-y-1">
                      <h3 className="font-display text-lg font-bold text-navy">
                        {tp(`exportForm.${fi}.name`, form.name)}
                      </h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {tp(`exportForm.${fi}.summary`, form.summary)}
                      </p>
                    </div>

                    {/* Weight bands stay untranslated — same rule as sizeGroups.weight. */}
                    {form.sizes && form.sizes.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                          {t("productDetail.exportForms.sizesHeading")}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {form.sizes.map((size) => (
                            <span
                              key={size}
                              className="rounded-lg bg-surface-alt border border-border/60 px-2.5 py-1 text-xs font-bold text-navy"
                            >
                              {size}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {form.options && form.options.length > 0 && (
                      <div className="grid sm:grid-cols-2 gap-3">
                        {form.options.map((opt, oi) => (
                          <div
                            key={oi}
                            className="p-3 rounded-xl bg-surface-alt/60 border border-border/40 space-y-1"
                          >
                            <div className="text-xs font-bold text-navy">
                              {tp(`exportForm.${fi}.option.${oi}.label`, opt.label)}
                            </div>
                            <div className="text-xs text-muted-foreground leading-relaxed">
                              {tp(`exportForm.${fi}.option.${oi}.value`, opt.value)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {form.notes && (
                      <p className="text-xs text-muted-foreground leading-relaxed border-t border-border/60 pt-3">
                        {tp(`exportForm.${fi}.notes`, form.notes)}
                      </p>
                    )}
                  </div>
                ))}

                {product.exportDocuments && product.exportDocuments.length > 0 && (
                  <div className="rounded-2xl border border-border/80 p-6 glass-card space-y-3">
                    <h3 className="font-display text-lg font-bold text-navy">
                      {t("productDetail.exportForms.documentsHeading")}
                    </h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {t("productDetail.exportForms.documentsLead")}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {/* Certification names are proper nouns — not translated. */}
                      {product.exportDocuments.map((doc) => (
                        <span
                          key={doc}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-mint/15 border border-mint/30 px-2.5 py-1 text-xs font-bold text-navy"
                        >
                          <ShieldCheck className="h-3 w-3 text-primary" aria-hidden="true" />
                          {doc}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          {product.culinaryInfo && (
            <TabsContent forceMount value="culinary" className="mt-6">
              <div className="space-y-6">
                {/* Why Choose Section */}
                <div className="rounded-2xl border border-border/80 p-6 glass-card space-y-4">
                  <h2 className="font-display text-lg font-bold text-navy flex items-center gap-2">
                    <Award className="h-5 w-5 text-amber-500" />
                    {tp(
                      "culinary.whyChooseTitle",
                      product.culinaryInfo.whyChooseTitle ??
                        t("productDetail.culinary.whyChooseDefaultTitle"),
                    )}
                  </h2>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {product.culinaryInfo.whyChoosePoints?.map((pt, idx) => (
                      <div
                        key={idx}
                        className="p-4 rounded-xl bg-surface-alt/60 border border-border/40 space-y-1.5"
                      >
                        <h4 className="font-bold text-sm text-navy">
                          {tp(`culinary.point.${idx}.title`, pt.title)}
                        </h4>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {tp(`culinary.point.${idx}.desc`, pt.desc)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Flavor & Cooking Grid */}
                <div className="grid sm:grid-cols-2 gap-6">
                  {product.culinaryInfo.cookingSuggestions && (
                    <div className="rounded-2xl border border-border/80 p-6 glass-card space-y-3">
                      <h3 className="font-display text-base font-bold text-navy flex items-center gap-2">
                        <Flame className="h-4 w-4 text-orange-500" />
                        {t("productDetail.culinary.cookingSuggestionsHeading")}
                      </h3>
                      <ul className="space-y-2 text-xs">
                        {product.culinaryInfo.cookingSuggestions.map((item, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-navy font-medium">
                            <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                            <span>{tp(`culinary.cooking.${idx}`, item)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {product.culinaryInfo.flavorProfile && (
                    <div className="rounded-2xl border border-border/80 p-6 glass-card space-y-3">
                      <h3 className="font-display text-base font-bold text-navy flex items-center gap-2">
                        <Utensils className="h-4 w-4 text-primary" />
                        {t("productDetail.culinary.flavorProfileHeading")}
                      </h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {tp("culinary.flavorProfile", product.culinaryInfo.flavorProfile)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Size Groups Table */}
                {product.culinaryInfo.sizeGroups && (
                  <div className="rounded-2xl border border-border/80 p-6 glass-card space-y-4">
                    <h3 className="font-display text-lg font-bold text-navy">
                      {t("productDetail.culinary.sizeGroupsHeading")}
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[480px] text-xs text-start">
                        <thead className="bg-surface-alt text-navy font-bold border-b border-border/60">
                          <tr>
                            <th className="px-4 py-3">
                              {t("productDetail.culinary.sizeGroupCol")}
                            </th>
                            <th className="px-4 py-3">
                              {t("productDetail.culinary.weightRangeCol")}
                            </th>
                            <th className="px-4 py-3">
                              {t("productDetail.culinary.primaryApplicationCol")}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border/40">
                          {product.culinaryInfo.sizeGroups.map((sg, idx) => (
                            <tr key={idx} className="hover:bg-surface-alt/40 transition-colors">
                              <td className="px-4 py-2.5 font-semibold text-navy">
                                {tp(`culinary.size.${idx}.group`, sg.group)}
                              </td>
                              <td className="px-4 py-2.5 font-bold text-primary">{sg.weight}</td>
                              <td className="px-4 py-2.5 text-muted-foreground">
                                {tp(`culinary.size.${idx}.application`, sg.application)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          <TabsContent forceMount value="apps" className="mt-6">
            <div className="rounded-2xl border border-border/80 p-6 glass-card space-y-4">
              <h2 className="font-display text-lg font-bold text-navy">
                {t("productDetail.applications.heading")}
              </h2>
              <ul className="grid sm:grid-cols-2 gap-3 text-xs">
                {tpArray("applications", product.applications).map((a, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-2 p-3 rounded-xl bg-surface-alt/60 border border-border/40 font-medium text-navy"
                  >
                    <Check className="h-4 w-4 text-emerald-500 shrink-0" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          </TabsContent>

          <TabsContent forceMount value="storage">
            <div className="glass-card rounded-2xl mt-4 p-6 border border-border/80 text-xs leading-relaxed text-navy/80 space-y-4">
              {/* This panel had no heading at all — the only one that didn't. */}
              <h2 className="font-display text-lg font-bold text-navy">
                {t("productDetail.tabs.storage")}
              </h2>
              <p className="font-semibold text-navy">{tp("storage", product.storage)}</p>
              <div className="grid sm:grid-cols-3 gap-3 pt-2">
                <div className="p-3 rounded-xl bg-surface-alt border border-border/60">
                  <div className="text-[10px] uppercase font-bold text-muted-foreground">
                    {t("productDetail.storage.coldChainTempLabel")}
                  </div>
                  <div className="font-bold text-navy text-xs mt-1">
                    {t("productDetail.storage.coldChainTempValue")}
                  </div>
                </div>
                <div className="p-3 rounded-xl bg-surface-alt border border-border/60">
                  <div className="text-[10px] uppercase font-bold text-muted-foreground">
                    {t("productDetail.storage.logisticsModeLabel")}
                  </div>
                  <div className="font-bold text-navy text-xs mt-1">
                    {t("productDetail.storage.logisticsModeValue")}
                  </div>
                </div>
                <div className="p-3 rounded-xl bg-surface-alt border border-border/60">
                  <div className="text-[10px] uppercase font-bold text-muted-foreground">
                    {t("productDetail.storage.documentationLabel")}
                  </div>
                  <div className="font-bold text-navy text-xs mt-1">
                    {t("productDetail.storage.documentationValue")}
                  </div>
                </div>
              </div>

              {/* How it can actually arrive. The three tiles above say what the
                  cold chain holds; they never said which delivery a buyer can
                  ask for, which is the part that changes an enquiry. */}
              {tpArray("deliveryOption", product.deliveryOptions ?? []).length > 0 && (
                <div className="border-t border-border/60 pt-4 space-y-2">
                  <h3 className="font-display text-sm font-bold text-navy">
                    {t("productDetail.storage.deliveryHeading")}
                  </h3>
                  <ul className="grid sm:grid-cols-2 gap-2">
                    {tpArray("deliveryOption", product.deliveryOptions ?? []).map((d, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Truck
                          className="h-3.5 w-3.5 shrink-0 text-primary mt-0.5"
                          aria-hidden="true"
                        />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </Section>

      {/* SV12: Japanese origin & cold-chain shipment section */}
      {product.slug === "super-fresh-chlorella-v12" && (
        <Section className="border-t border-border/60 pt-16 mt-8">
          <div className="grid lg:grid-cols-12 gap-10 items-stretch">
            <div className="lg:col-span-5">
              <AnimatedJapanShipment />
            </div>
            <div className="lg:col-span-7 space-y-5">
              <div className="space-y-2">
                <span className="inline-flex items-center gap-2 text-xs font-bold text-primary uppercase tracking-widest">
                  <img
                    src={japanFlagSvg}
                    alt={t("common.alt.flagJapan")}
                    className="h-3.5 w-5 rounded object-cover shadow-sm border border-border/40"
                  />
                  {t("productDetail.sv12Japan.badge")}
                </span>
                <h2 className="font-display text-2xl font-bold text-navy">
                  {t("productDetail.sv12Japan.heading")}
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t("productDetail.sv12Japan.subheading")}
                </p>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                {(
                  [
                    { icon: Factory, key: "origin" },
                    { icon: Plane, key: "transit" },
                    { icon: Snowflake, key: "arrival" },
                  ] as const
                ).map(({ icon: Icon, key }) => (
                  <div
                    key={key}
                    className="p-4 rounded-xl bg-surface-alt border border-border/60 space-y-2"
                  >
                    <Icon className="h-5 w-5 text-primary" />
                    <div className="text-xs font-bold text-navy">
                      {t(`productDetail.sv12Japan.${key}.title`)}
                    </div>
                    <p className="text-[11px] leading-relaxed text-navy/70">
                      {t(`productDetail.sv12Japan.${key}.body`)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-12 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5 space-y-4">
              <h3 className="font-display text-xl font-bold text-navy">
                {t("productDetail.sv12Japan.bio.heading")}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t("productDetail.sv12Japan.bio.p1")}
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t("productDetail.sv12Japan.bio.p2")}
              </p>
            </div>
            <div className="lg:col-span-7">
              <AnimatedChlorellaBiology />
            </div>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="glass-card rounded-xl p-4 border border-border/80">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  {t(`productDetail.sv12Japan.facts.${i}.label`)}
                </div>
                <div className="mt-1 text-xs font-semibold text-navy leading-relaxed">
                  {t(`productDetail.sv12Japan.facts.${i}.value`)}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Decapsulated Artemia: Shell vs Embryo Anatomical Science & Nutrition Infographic */}
      {product.slug === "decapsulated-dry-artemia-cysts" && (
        <Section className="border-t border-border/60 pt-16 mt-8 space-y-12">
          <AnimatedDecapIllustration />
          <DecapNutritionalInfographic />
        </Section>
      )}

      {/* Why this supplier. The retired product pages carried a block like this
          — "Product Advantages" plus "Why Work With VARS Aquaculture?" — and it
          was the only place the multi-origin sourcing model, the cold-chain
          handling and the post-delivery technical support were ever stated.
          None of it was ported when these pages were rebuilt. */}
      {product.advantages && product.advantages.length > 0 && (
        <Section className="border-t border-border/60 pt-16 mt-8">
          <div className="space-y-6">
            <h2 className="font-display text-2xl font-bold text-navy">
              {t("productDetail.advantages.heading")}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {product.advantages.map((adv, i) => (
                <div
                  key={i}
                  className="glass-card rounded-2xl border border-border/80 p-5 space-y-2"
                >
                  <h3 className="font-display text-base font-bold text-navy flex items-start gap-2">
                    <ShieldCheck
                      className="h-4 w-4 shrink-0 text-primary mt-0.5"
                      aria-hidden="true"
                    />
                    <span>{tp(`advantage.${i}.title`, adv.title)}</span>
                  </h3>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    {tp(`advantage.${i}.desc`, adv.desc)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* Product FAQ & Incubation Guide Widget Section */}
      <Section className="border-t border-border/60 pt-16 mt-8">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* FAQ Accordion Column */}
          <div className="lg:col-span-7 space-y-6">
            <div className="space-y-2">
              <span className="text-xs font-bold text-primary uppercase tracking-widest flex items-center gap-1.5">
                <HelpCircle className="h-4 w-4" /> {t("productDetail.faq.eyebrow")}
              </span>
              <h2 className="font-display text-2xl font-bold text-navy">
                {t("productDetail.faq.heading")}
              </h2>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {t("productDetail.faq.introPrefix")} {tp("title", product.title)}.
              </p>
            </div>

            <Accordion type="single" collapsible className="w-full space-y-3">
              {/* One source, not two. This block used to rebuild the key logic
                  that productFaqPairs() already implements for the FAQPage
                  JSON-LD — two copies of the same rules, and ADR-012 requires
                  the markup and the visible accordion to derive from the same
                  function precisely so they cannot drift apart. */}
              {productFaqPairs(product.slug).map((pair, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="glass-card rounded-xl px-5 border border-border/80"
                >
                  <AccordionTrigger className="text-start font-display text-sm font-semibold text-navy hover:no-underline py-4">
                    {pair.q}
                  </AccordionTrigger>
                  <AccordionContent
                    forceMount
                    className="text-xs text-muted-foreground leading-relaxed pb-4"
                  >
                    {pair.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Dynamic Technical & Hatchery Widget Column */}
          {(() => {
            const widget = getProductWidgetData(product, t);
            return (
              <div className="lg:col-span-5 space-y-6">
                <div className="glass-card rounded-3xl border border-primary/20 bg-gradient-to-b from-teal/10 via-background to-navy/5 p-6 md:p-8 space-y-6 shadow-xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 transform translate-x-4 -translate-y-4 h-32 w-32 rounded-full bg-primary/10 blur-2xl pointer-events-none" />

                  <div className="flex items-center gap-2 text-xs font-bold text-primary uppercase tracking-wider">
                    <BookOpen className="h-4 w-4 text-mint-ink" />
                    <span>{widget.badge}</span>
                  </div>

                  <h3 className="font-display text-xl font-bold text-navy">{widget.title}</h3>

                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {widget.description}
                  </p>

                  <div className="grid grid-cols-2 gap-3 py-2 text-xs">
                    <div className="p-3 rounded-xl bg-background/80 border border-border/60">
                      <div className="text-muted-foreground text-[10px] font-semibold">
                        {widget.metric1Label}
                      </div>
                      <div className="font-bold text-navy text-xs mt-0.5">
                        {widget.metric1Value}
                      </div>
                    </div>
                    <div className="p-3 rounded-xl bg-background/80 border border-border/60">
                      <div className="text-muted-foreground text-[10px] font-semibold">
                        {widget.metric2Label}
                      </div>
                      <div className="font-bold text-navy text-xs mt-0.5">
                        {widget.metric2Value}
                      </div>
                    </div>
                  </div>

                  <Link to={widget.linkTo} className="w-full block">
                    <Button className="w-full rounded-xl gap-2 font-bold shadow-lg shadow-primary/20 text-xs cursor-pointer">
                      <span>{widget.buttonText}</span>
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>

                  {widget.secondaryLinkTo && (
                    <Link
                      to={widget.secondaryLinkTo}
                      className="flex items-center justify-center gap-1.5 text-xs font-bold text-primary hover:underline"
                    >
                      {widget.secondaryLinkText} <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </Section>

      <RelatedProductsWidget currentProduct={product} />

      <QuoteDrawer
        open={quoteOpen}
        onOpenChange={setQuoteOpen}
        productSlug={product.slug}
        productTitle={tp("title", product.title)}
      />
    </>
  );
}

const PRODUCT_FAQS: Record<string, Array<{ q: string; a: string }>> = {
  artemia: [
    {
      q: "What are the optimal hatching parameters for Revive Artemia Cysts?",
      a: "For optimal ≥ 85% hatch rates, maintain water salinity at 25–35 ppt (g/L), temperature at 28–30 °C, pH between 8.0–8.5, and provide continuous strong bottom aeration under 2,000 Lux light.",
    },
    {
      q: "How many hours does full Artemia hatch-out take?",
      a: "Under recommended conditions (28–30 °C), initial hatching begins at 15–18 hours, reaching maximum hatch-out efficiency within 20–24 hours.",
    },
    {
      q: "Where can I view the step-by-step incubation procedure?",
      a: "Our complete incubation manual, including tank density, aeration setup, and nauplii separation, is available in the Artemia Incubation Guide.",
    },
  ],
  "decapsulated-dry-artemia-cysts": [
    {
      q: "Do decapsulated Artemia cysts require incubation or hatching?",
      a: "No hatching is required! Decapsulated cysts have the indigestible outer shell removed and can be fed directly to larvae and post-larvae with zero shell contamination.",
    },
    {
      q: "What is the nutritional value compared to live nauplii?",
      a: "Decapsulated cysts retain 100% of their embryonic energy reserves (61% protein, 24.5% lipids, 502 kcal/100g), delivering higher caloric value than hatched nauplii.",
    },
  ],
  "super-fresh-chlorella-v12": [
    {
      q: "What makes Super Fresh Chlorella V12 different from dry algae powders?",
      a: "SV-12 is 100% live, fresh liquid Chlorella concentrate (~13 billion cells/ml) shipped under continuous 2–6 °C cold chain. Cell walls remain intact and live, delivering maximum DHA/EPA/B12 bio-availability to rotifer and fish larval cultures.",
    },
    {
      q: "How does SV-12 accelerate rotifer culture multiplication?",
      a: "The live 3–6 µm cell size and rich lipid profile allow rotifers to achieve up to 8-fold population multiplication within 3 days without settling or fouling culture water.",
    },
    {
      q: "Where is SV12 produced and how is it shipped?",
      a: "SV12 is cultured by Chlorella Industry Co., Ltd. at the Chikugo plant in Fukuoka, Japan, and air-freighted in insulated styrene-foam boxes with cold packs under a 2–6 °C cold chain, so the algae arrive alive and fresh.",
    },
  ],
  "emerald-chlorella-powder": [
    {
      q: "How is Emerald Chlorella Powder reconstituted in hatchery tanks?",
      a: "Mix 50–100 g per 1,000 L of hatchery water under continuous aeration. High Vitamin B12 (1000 µg/100g) and 60.6% protein content provide excellent green-water conditioning and copepod enrichment.",
    },
  ],
  "atlantic-salmon-egg": [
    {
      q: "How are fertilized salmon eggs delivered while preserving biosecurity?",
      a: "Salmon ova are shipped in temperature-controlled insulated containers with continuous digital temperature loggers maintaining 4–8 °C throughout air transport.",
    },
    {
      q: "What incubation water temperature is recommended upon arrival?",
      a: "Transfer eyed salmon eggs into incubation trays supplied with clean, well-oxygenated freshwater maintained at 4–8 °C.",
    },
  ],
  "coho-salmon-egg": [
    {
      q: "Are Coho salmon eggs certified disease-free?",
      a: "Yes, all Coho salmon eggs are SPF certified and tested free of IPNV, VHSV, and IHNV pathogens under strict biosecurity protocols.",
    },
  ],
  "vital-wheat-gluten": [
    {
      q: "What is the binding strength and water stability of VARS Vital Wheat Gluten?",
      a: "With ≥ 150% water absorption capacity and ≥ 82% protein content, it forms a cohesive viscoelastic matrix that prevents pellet disintegration and nutrient leaching in extruded fish and shrimp diets.",
    },
    {
      q: "Is VARS Vital Wheat Gluten suitable for marine finfish and shrimp feeds?",
      a: "Yes, it is widely used as a highly digestible plant protein binder in salmonid, sea bass, sea bream, and shrimp feeds to replace expensive synthetic binders.",
    },
  ],
  "fish-meal": [
    {
      q: "What pelagic species are used in VARS Steam-Dried Fish Meal?",
      a: "Sourced from wild-caught pelagic species and trimmings, from producers certified to IFFO-RS and operating under EU sustainability rules, delivering 55–65% crude protein and balanced essential amino acids.",
    },
  ],
  "potato-protein-meal": [
    {
      q: "How much fish meal can be substituted with VARS Potato Protein Meal in aquafeed?",
      a: "Trial data shows up to 20% of marine fish meal can be replaced in sea bass and sea bream diets while maintaining equivalent feed conversion ratios (FCR) and 92% protein digestibility.",
    },
  ],
  "krill-meal-bio-additives": [
    {
      q: "Does Krill Meal enhance fillet pigmentation in salmon and trout?",
      a: "Yes! Rich in natural astaxanthin (≥ 150 ppm) bound to marine phospholipids, it enhances flesh color, immune resilience, and feeding response in broodstock and fry.",
    },
  ],
  "mediterranean-sea-bass": [
    {
      q: "What product forms are available for wholesale export of Mediterranean Sea Bass?",
      a: "We export Whole Round, Descaled & Gutted (D&G), Dressed, and Fresh/Frozen Fillets in standard sizes (300–400g, 400–600g, 600–800g, 800–1000g+).",
    },
    {
      q: "What cold-chain logistics options are used for EU & MENA delivery?",
      a: "Shipped in styrofoam boxes with gel packs or flake ice via temperature-controlled air freight (0–2 °C) or reefer trucks (−18 °C for frozen).",
    },
  ],
  "mediterranean-sea-bream": [
    {
      q: "Are VARS Mediterranean Sea Bream certified by ASC and GlobalG.A.P.?",
      a: "Yes, all sea bream are cultured in open-sea cages with pristine water exchange, certified compliant with ASC and GlobalG.A.P export standards.",
    },
  ],
  meagre: [
    {
      q: "What is the flavor and culinary profile of VARS Meagre (Argyrosomus regius)?",
      a: "Meagre offers a mild, sweet flavor and firm white flesh with low fat and high protein content (18g/100g), ideal for whole fish grilling, salt-crust roasting, and fine dining fillets.",
    },
    {
      q: "How does Meagre compare to Mediterranean Sea Bass and Sea Bream in kitchen yield?",
      a: "Meagre yields thicker, meatier fillet cuts that hold their architectural form under high heat without flaking apart or drying out, providing higher edible portion recovery and culinary versatility across European and Middle Eastern fine dining menus.",
    },
    {
      q: "What is the difference between Meagre (Argyrosomus regius) and Brown Meagre (Sciaena umbra)?",
      a: "Argyrosomus regius (sarıağız in Turkish, corvina in Spanish) is a large, commercially farmed Mediterranean species reaching 1–3 kg+ portion sizes with continuous 52-week availability. Brown Meagre (Sciaena umbra) is a separate, small wild reef fish not commercially farmed; all VARS consignments are certified Argyrosomus regius.",
    },
  ],
  amberjack: [
    {
      q: "Is VARS Amberjack (Seriola dumerili) suitable for raw sashimi applications?",
      a: "Yes, our Turkey-origin Greater Amberjack is harvested under strict biosecurity protocols and immediate slush-ice chilling, providing sashimi-grade firm texture for HORECA and fine dining.",
    },
  ],
  "olive-flounder": [
    {
      q: "How is Olive Flounder (Paralichthys olivaceus) packaged for air freight?",
      a: "Shipped fresh on ice or live in oxygen-injected marine containers, engineered for 52-week continuous supply to high-end sushi restaurants and distributors.",
    },
  ],
  "rainbow-trout": [
    {
      q: "What product forms and sizes are available for Rainbow Trout export?",
      a: "Available whole round, gutted, and filleted (200–300g portion size up to 1kg+ executive cuts) for smoking, retail, and HORECA supply.",
    },
  ],
};

/**
 * The questions a product page answers when it has no set of its own.
 *
 * Slugs into the knowledge base rather than copies of it. The MOQ answer used
 * to be written out here a second time and had already drifted from the
 * canonical one in faqs.ts — this file said "1 carton for live feed, 50,000 for
 * salmon eggs, and 500 kg for seafood", omitting the full-pallet rule for feed
 * ingredients that the real answer carries. ADR-012 exists for exactly this.
 *
 * `import-permits` is new to product pages. "Can you ship to my country?" was
 * one of the four questions the retired product pages answered and none of
 * these pages did. It is answered from the permit angle rather than through
 * `shipping-countries`, whose "10+ countries" figure is the kind of unverified
 * statistic ADR-010 removed from marketing copy — not something to propagate
 * across eighteen more pages on the way past.
 */
// `cold-chain` was here until VARS-029 removed that entry from the knowledge
// base — it guaranteed cold-chain integrity, which nothing backed. `lead-times`
// answers the same "when and how does it reach me" question without promising
// an outcome. Note this list throws on an unknown slug where faqsForPage() only
// drops it, so a removal in faqs.ts breaks SSR on every product page until this
// is updated too.
const DEFAULT_FAQ_SLUGS = ["moq", "lead-times", "coa", "import-permits"];

const DEFAULT_FAQS = DEFAULT_FAQ_SLUGS.map((slug) => {
  const entry = FAQS.find((faq) => faq.slug === slug);
  if (!entry) throw new Error(`Unknown default product FAQ slug: ${slug}`);
  return entry;
});

const ARTEMIA_HATCHING_STEPS = [
  {
    step: "Step 1",
    title: "Prepare Incubation Solution",
    content:
      "Prepare a saline solution using sea salt at 28–30 ppt (g/L) or UV/ozone treated filtered seawater. Adjust pH to 7.9–8.5 (add 2 g Sodium Bicarbonate NaHCO₃ per liter if pH is lower). Maintain water temperature at 27–29 °C.",
  },
  {
    step: "Step 2",
    title: "Prepare Decapsulation Solution",
    content:
      "Standard ratio for 454 g (1 lb) of dry cysts: use 1.5 L Sodium Hypochlorite (NaOCl - 10% active chlorine) combined with 34–50 ml saturated NaOH solution.",
  },
  {
    step: "Step 3",
    title: "Hydration Phase",
    content:
      "Place dry Artemia cysts in clean freshwater under strong aeration for 1 hour at 20–25 °C. Hydration is complete when cysts change from biconcave disk shape to fully spherical.",
  },
  {
    step: "Step 4",
    title: "First Decapsulation Bath",
    content:
      "Transfer hydrated cysts into 0.75 L of decapsulation solution. Stir continuously for 25–40 minutes until the outer brown shell dissolves and turns bright orange.",
  },
  {
    step: "Step 5",
    title: "Separation & Washing",
    content:
      "Immediately strain the decapsulated embryos through a 125 µm sieve mesh and rinse thoroughly with clean tap water or seawater to remove residual chlorine.",
  },
  {
    step: "Step 6",
    title: "Second Decapsulation (Optional)",
    content:
      "If shell fragments remain, perform a brief 3–5 minute secondary wash bath in diluted NaOCl solution before final rinsing.",
  },
  {
    step: "Step 7",
    title: "Chemical Dechlorination & Activation",
    content:
      "Neutralize active chlorine by dipping strained cysts into a 0.1% sodium thiosulfate (Na₂S₂O₃) or ascorbic acid solution for 1 minute.",
  },
  {
    step: "Step 8",
    title: "Conical Reactor Incubation",
    content:
      "Transfer activated cysts into a conical hatching vessel filled with 28–30 ppt seawater at 28–30 °C. Provide continuous bottom aeration and 2,000 Lux continuous light for 20–24 hours.",
  },
  {
    step: "Step 9",
    title: "Harvesting & Nauplii Collection",
    content:
      "Turn off aeration for 10–15 minutes. Empty shells float to the surface while live phototactic nauplii concentrate at the bottom light source for easy siphoning.",
  },
];

interface TableCategory {
  title: string;
  rows: Array<{ col1: string; col2: string; col3?: string; col4?: string }>;
  headers?: string[];
}

const PRODUCT_DETAILED_TABLES: Record<string, TableCategory[]> = {
  "super-fresh-chlorella-v12": [
    {
      title: "Nutritional Composition (per 100g dry net weight)",
      headers: ["Component", "Amount"],
      rows: [
        { col1: "Protein", col2: "54.80%" },
        { col1: "Fat (Lipids)", col2: "13.20%" },
        { col1: "Fibre", col2: "0.50%" },
        { col1: "CHO (Carbohydrates)", col2: "24.30%" },
        { col1: "Ash (Minerals)", col2: "7.20%" },
      ],
    },
    {
      title: "Essential Minerals",
      headers: ["Mineral", "Amount (mg/100g)"],
      rows: [
        { col1: "Calcium (Ca)", col2: "250 mg" },
        { col1: "Magnesium (Mg)", col2: "240 mg" },
        { col1: "Iron (Fe)", col2: "150 mg" },
        { col1: "Potassium (K)", col2: "1,000 mg" },
        { col1: "Phosphorus (P)", col2: "1,950 mg" },
      ],
    },
    {
      title: "Fatty Acid Profile (% of total lipid; total lipid 13.2%)",
      headers: ["Fatty Acid", "Lipid No.", "% of Lipids"],
      rows: [
        { col1: "Myristic Acid", col2: "C14:0", col3: "0.60%" },
        { col1: "Palmitic Acid", col2: "C16:0", col3: "14.40%" },
        { col1: "Palmitoleic Acid", col2: "C16:1", col3: "2.00%" },
        { col1: "Hexadecadienoic Acid", col2: "C16:2", col3: "8.10%" },
        { col1: "Stearic Acid", col2: "C18:0", col3: "0.60%" },
        { col1: "Linoleic Acid (ω6)", col2: "C18:2", col3: "18.80%" },
        { col1: "Linolenic Acid (ω3)", col2: "C18:3", col3: "13.70%" },
        { col1: "EPA (Eicosapentaenoic)", col2: "C20:5", col3: "8.50%" },
        { col1: "DPA (Docosapentaenoic)", col2: "C22:5", col3: "4.00%" },
        { col1: "DHA (Docosahexaenoic)", col2: "C22:6", col3: "15.00%" },
      ],
    },
    {
      title: "Vitamins & Bioactive Compounds",
      headers: ["Compound", "Concentration"],
      rows: [
        { col1: "Vitamin B12", col2: "> 320 µg/l (0.4 mg/100g)" },
        { col1: "Vitamin B2 (Riboflavin)", col2: "6.5 mg/100g" },
        { col1: "Vitamin B6", col2: "1.0 mg/100g" },
        { col1: "Vitamin C", col2: "51 mg/100g" },
        { col1: "Niacin", col2: "20 mg/100g" },
        { col1: "γ-Tocopherol (Vitamin E)", col2: "19 mg/100g" },
        { col1: "Chlorophyll", col2: "2.6 mg/100g" },
        { col1: "Carotene", col2: "68 mg/100g" },
        { col1: "Xanthophyll", col2: "91 mg/100g" },
        { col1: "Inositol", col2: "200 mg/100g" },
        { col1: "Choline", col2: "350 mg/100g" },
      ],
    },
    {
      title: "Amino Acid Profile",
      headers: ["Amino Acid", "g / 100g"],
      rows: [
        { col1: "Arginine", col2: "2.82 g" },
        { col1: "Lysine", col2: "2.82 g" },
        { col1: "Histidine", col2: "1.05 g" },
        { col1: "Phenylalanine", col2: "4.80 g" },
        { col1: "Tyrosine", col2: "1.51 g" },
        { col1: "Leucine", col2: "3.92 g" },
        { col1: "Isoleucine", col2: "1.98 g" },
        { col1: "Methionine", col2: "0.55 g" },
        { col1: "Valine", col2: "2.87 g" },
        { col1: "Alanine", col2: "3.70 g" },
        { col1: "Glycine", col2: "2.95 g" },
        { col1: "Proline", col2: "1.60 g" },
        { col1: "Glutamic Acid", col2: "7.35 g" },
        { col1: "Serine", col2: "1.89 g" },
        { col1: "Threonine", col2: "2.06 g" },
        { col1: "Aspartic Acid", col2: "4.37 g" },
        { col1: "Tryptophan", col2: "1.04 g" },
        { col1: "Cystine", col2: "0.51 g" },
      ],
    },
    {
      title: "Lipid Classes Distribution",
      headers: ["Class", "Proportion"],
      rows: [
        { col1: "Glycolipid", col2: "36%" },
        { col1: "Phospholipid", col2: "25%" },
        { col1: "Neutral Lipid", col2: "36%" },
      ],
    },
  ],
  artemia: [
    {
      title: "Physical & Hatching Specifications",
      headers: ["Component", "Value"],
      rows: [
        { col1: "Moisture Content", col2: "< 9.0%" },
        { col1: "Crude Protein", col2: "55.0%" },
        { col1: "Crude Fat", col2: "12.0%" },
        { col1: "Cysts Per Gram (CPG)", col2: "210,000" },
        { col1: "Typical Hatching Rate", col2: "≥ 85%" },
      ],
    },
    {
      title: "Omega-3 Essential Fatty Acid Profile",
      headers: ["Fatty Acid", "Lipid No.", "% of Lipids", "mg / 100g"],
      rows: [
        { col1: "Alpha-Linolenic Acid (ALA)", col2: "C18:3", col3: "24.8%", col4: "3,931.7 mg" },
        { col1: "Eicosapentaenoic Acid (EPA)", col2: "C20:5", col3: "6.5%", col4: "1,028.7 mg" },
        { col1: "Docosahexaenoic Acid (DHA)", col2: "C22:6", col3: "0.1%", col4: "20.8 mg" },
      ],
    },
    {
      title: "Omega-6 Essential Fatty Acid Profile",
      headers: ["Fatty Acid", "Lipid No.", "% of Lipids", "mg / 100g"],
      rows: [
        { col1: "Linoleic Acid (LA)", col2: "C18:2", col3: "5.3%", col4: "834.0 mg" },
        { col1: "Arachidonic Acid (AA)", col2: "C20:4", col3: "1.0%", col4: "160.4 mg" },
      ],
    },
  ],
  "decapsulated-dry-artemia-cysts": [
    {
      title: "Decapsulated Shell-Free Specifications",
      headers: ["Parameter", "Laboratory Standard"],
      rows: [
        { col1: "Form", col2: "Dry shell-free decapsulated cysts" },
        { col1: "Chorion Shell Content", col2: "0% (Zero shell contamination)" },
        { col1: "Light & Heavy Impurities", col2: "0% Light / 0% Heavy" },
        { col1: "Moisture Control", col2: "4.0 – 5.0%" },
        { col1: "Crude Protein", col2: "61.0%" },
        { col1: "Crude Fat", col2: "24.5%" },
        { col1: "Caloric Energy Density", col2: "502 kcal / 100g" },
      ],
    },
  ],
};

/**
 * Rotifer cultivation protocols, from the manufacturer.
 *
 * Both Chlorella products exist to grow rotifers — PT Aqua files them under
 * /rotifer-cultivation/ and calls the Emerald line "Emerald Rotifer Diet" —
 * while this site described them by their ingredient and left the application
 * to be inferred. These are the manufacturer's own published parameters:
 * ptaqua.eu/rotifer-cultivation/super-fresh-chlorella/ and
 * .../emerald-rotifer-diet/.
 *
 * Deliberately NOT extended to the two Artemia products. Artemia is the feed
 * stage AFTER rotifers, not a rotifer feed, and conflating the two is the
 * error docs/project/test-plan.md records from 2026-07-28.
 */
interface RotiferProtocol {
  intro: string;
  conditions: { label: string; value: string }[];
  steps?: string[];
  tables: TableCategory[];
  note?: string;
}

const ROTIFER_PROTOCOLS: Record<string, RotiferProtocol> = {
  "super-fresh-chlorella-v12": {
    intro:
      "SV-12 was developed specifically for rotifer cultivation. The schedule below is the manufacturer's suggested plan for an S-type rotifer culture, reproduced as they publish it.",
    conditions: [
      { label: "Rotifer strain", value: "S-type" },
      { label: "Tank volume", value: "10 T" },
      { label: "Water temperature", value: "24 °C" },
    ],
    tables: [
      {
        title: "Suggested Rotifer Cultivation Plan",
        headers: ["Stage", "Rotifer density (per ml)", "SV-12 dosage"],
        rows: [
          { col1: "First dosage", col2: "1,000", col3: "1.5 L morning · 1.5 L evening" },
          { col1: "Day 1", col2: "2,000", col3: "1.5 L morning · 1.5 L evening" },
          { col1: "Day 2", col2: "4,000", col3: "2.0 L morning · 2.0 L evening" },
          { col1: "Day 3", col2: "8,000", col3: "Per the rotifer analysis below" },
        ],
      },
      {
        // Labelled for what it is. This is the analysis of ROTIFERS grown on
        // SV-12, not of SV-12 itself — the product's own fatty acid profile is
        // in the composition tab and carries different figures.
        title: "Typical Analysis of Rotifers Cultured on SV-12",
        headers: ["Fraction", "Value", "Basis"],
        rows: [
          { col1: "Lipid total", col2: "12.7%", col3: "Ratio to dry net weight" },
          { col1: "EPA (20:5)", col2: "4.1%", col3: "% of total fatty acids" },
          { col1: "DPA (22:5)", col2: "5.8%", col3: "% of total fatty acids" },
          { col1: "DHA (22:6)", col2: "10.5%", col3: "% of total fatty acids" },
        ],
      },
    ],
  },
  "emerald-chlorella-powder": {
    intro:
      "Emerald is a rotifer diet: a spray-dried Chlorella powder for high- and low-density rotifer cultivation, in batch or continuous production, and for the green-water technique. The manufacturer's preparation and feeding protocol follows.",
    conditions: [
      { label: "Water temperature", value: "26.5 – 28 °C" },
      { label: "Salinity", value: "18 – 35 ppt, by local strain and conditions" },
      { label: "Dissolved oxygen", value: "Over 4 ppm" },
      { label: "Initial rotifer density", value: "300 – 500 ind./ml" },
    ],
    steps: [
      "Blend Emerald with fresh water using an electric mixer for 5 minutes.",
      "Allow it to settle for 5 minutes, then mix again for 2 minutes.",
      "Keep the maximum mixing ratio at or below 120 g of powder per litre.",
      "Store the prepared suspension refrigerated and use it within 48 hours.",
      "Feed with a peristaltic pump for continuous feeding, or 4–6 times a day intermittently.",
    ],
    tables: [
      {
        title: "Feeding Amounts (L-type Rotifers)",
        headers: ["Day", "Feeding rate"],
        rows: [
          { col1: "Day 1", col2: "0.6 – 0.8 g / million rotifers / day" },
          { col1: "Day 2", col2: "0.5 – 0.6 g / million rotifers / day" },
          { col1: "Day 3", col2: "0.5 – 0.6 g / million rotifers / day" },
          { col1: "Day 4", col2: "Harvest" },
        ],
      },
    ],
  },
};

const AQUARIUM_TARGETED_SLUGS = ["artemia", "decapsulated-dry-artemia-cysts"];

function getProductWidgetData(product: Product, t: (key: string) => string) {
  const aquariumCallout = AQUARIUM_TARGETED_SLUGS.includes(product.slug)
    ? {
        secondaryLinkTo: "/aquariums-and-hobbyists",
        secondaryLinkText: t("widget.aquariumCallout"),
      }
    : {};

  const isChlorellaProduct =
    product.slug === "emerald-chlorella-powder" || product.slug === "super-fresh-chlorella-v12";

  if (isChlorellaProduct) {
    return {
      badge: t("widget.liveFeed.badge"),
      title: t("widget.chlorella.title"),
      description: t("widget.chlorella.description"),
      metric1Label: t("widget.chlorella.metric1Label"),
      metric1Value:
        product.metrics.find((m) => m.label.toLowerCase().includes("density"))?.value ??
        product.metrics.find((m) => m.label.includes("Protein"))?.value ??
        t("widget.seeSpecifications"),
      metric2Label: t("widget.chlorella.metric2Label"),
      metric2Value: t("widget.chlorella.metric2Value"),
      linkTo: "/artemia-cysts-incubation-guide",
      buttonText: t("widget.chlorella.buttonText"),
      ...aquariumCallout,
    };
  }

  if (
    product.category === "live-feed-aquaculture" ||
    product.slug === "decapsulated-dry-artemia-cysts"
  ) {
    return {
      badge: t("widget.liveFeed.badge"),
      title: t("widget.artemia.title"),
      description: t("widget.artemia.description"),
      metric1Label: t("widget.artemia.metric1Label"),
      metric1Value: "25 – 35 ppt",
      metric2Label: t("widget.artemia.metric2Label"),
      metric2Value: "28 – 30 °C",
      linkTo: "/artemia-cysts-incubation-guide",
      buttonText: t("widget.artemia.buttonText"),
      ...aquariumCallout,
    };
  }

  if (product.category === "hatchery-solutions") {
    return {
      badge: t("widget.hatchery.badge"),
      title: t("widget.hatchery.title"),
      description: t("widget.hatchery.description"),
      metric1Label: t("widget.hatchery.metric1Label"),
      metric1Value: "4 – 8 °C",
      metric2Label: t("widget.hatchery.metric2Label"),
      metric2Value: t("widget.hatchery.metric2Value"),
      linkTo: "/salmonid-ova-solutions",
      buttonText: t("widget.hatchery.buttonText"),
    };
  }

  if (product.category === "feed-additives") {
    return {
      badge: t("widget.feed.badge"),
      title: t("widget.feed.title"),
      description: t("widget.feed.description"),
      metric1Label: t("widget.feed.metric1Label"),
      metric1Value: product.metrics.find((m) => m.label.includes("Protein"))?.value || "55–82%",
      metric2Label: t("widget.feed.metric2Label"),
      metric2Value: t("widget.feed.metric2Value"),
      linkTo: "/services-solutions",
      buttonText: t("widget.feed.buttonText"),
    };
  }

  // Seafood Export category
  return {
    badge: t("widget.seafood.badge"),
    title: t("widget.seafood.title"),
    description: t("widget.seafood.description").replace("{product}", product.title),
    metric1Label: t("widget.seafood.metric1Label"),
    metric1Value: "0 °C – 2 °C",
    metric2Label: t("widget.seafood.metric2Label"),
    metric2Value: "−18 °C",
    linkTo: "/horeca-seafood-middle-east",
    buttonText: t("widget.seafood.buttonText"),
  };
}
